// src/pages/carbon-offset-dashboard/components/OffsetHistoryTable.jsx
import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const OffsetHistoryTable = ({ offsetHistory, timeRange, onPurchaseOffset, eligibleShipments }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'bg-success-50 text-success-700 border-success-100';
      case 'pending': return 'bg-warning-50 text-warning-700 border-warning-100';
      case 'processing': return 'bg-primary-50 text-primary-700 border-primary-100';
      case 'failed': return 'bg-error-50 text-error-700 border-error-100';
      default: return 'bg-surface-50 text-text-secondary border-border';
    }
  };

  const getOffsetTypeIcon = (type) => {
    switch (type) {
      case 'Forestry': return 'Trees';
      case 'Renewable Energy': return 'Zap';
      case 'Blue Carbon': return 'Waves';
      case 'Direct Air Capture': return 'Wind';
      default: return 'Leaf';
    }
  };

  const getOffsetTypeColor = (type) => {
    switch (type) {
      case 'Forestry': return 'text-success';
      case 'Renewable Energy': return 'text-primary';
      case 'Blue Carbon': return 'text-secondary';
      case 'Direct Air Capture': return 'text-warning';
      default: return 'text-text-secondary';
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <Icon name="ArrowUpDown" size={14} className="text-text-tertiary" />;
    }
    return sortConfig.direction === 'asc' 
      ? <Icon name="ArrowUp" size={14} className="text-primary" />
      : <Icon name="ArrowDown" size={14} className="text-primary" />;
  };

  // Filter and sort data
  const filteredHistory = offsetHistory.filter(item => {
    if (filterStatus !== 'all' && item.status !== filterStatus) return false;
    if (filterType !== 'all' && item.offsetType !== filterType) return false;
    return true;
  });

  const sortedHistory = [...filteredHistory].sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const totalOffset = filteredHistory.reduce((sum, item) => sum + item.amount, 0);
  const totalCost = filteredHistory.reduce((sum, item) => sum + item.cost, 0);
  const verifiedOffset = filteredHistory.filter(item => item.status === 'verified').reduce((sum, item) => sum + item.amount, 0);

  return (
    <div className="bg-surface rounded-lg border border-border shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">Carbon Offset History</h2>
            <p className="text-sm text-text-secondary mt-1">
              Track and manage your carbon offset purchases and certificates
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm text-text-secondary">Status:</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="text-sm border border-border rounded px-2 py-1 bg-surface focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">All</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <label className="text-sm text-text-secondary">Type:</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="text-sm border border-border rounded px-2 py-1 bg-surface focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">All Types</option>
                <option value="Forestry">Forestry</option>
                <option value="Renewable Energy">Renewable Energy</option>
                <option value="Blue Carbon">Blue Carbon</option>
                <option value="Direct Air Capture">Direct Air Capture</option>
              </select>
            </div>
            
            <button
              onClick={() => onPurchaseOffset(null)}
              className="bg-success text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-success-600 transition-colors duration-150 flex items-center space-x-2"
            >
              <Icon name="Plus" size={16} />
              <span>New Offset</span>
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="bg-surface-50 rounded-lg p-4 border border-border-light">
            <div className="flex items-center space-x-2">
              <Icon name="Leaf" size={16} className="text-success" />
              <span className="text-sm font-medium text-text-secondary uppercase tracking-wide">Total Offset</span>
            </div>
            <p className="text-2xl font-bold text-text-primary mt-2">
              {totalOffset.toFixed(1)} <span className="text-sm font-normal text-text-secondary">tonnes CO2e</span>
            </p>
          </div>
          
          <div className="bg-surface-50 rounded-lg p-4 border border-border-light">
            <div className="flex items-center space-x-2">
              <Icon name="Shield" size={16} className="text-primary" />
              <span className="text-sm font-medium text-text-secondary uppercase tracking-wide">Verified</span>
            </div>
            <p className="text-2xl font-bold text-text-primary mt-2">
              {verifiedOffset.toFixed(1)} <span className="text-sm font-normal text-text-secondary">tonnes CO2e</span>
            </p>
          </div>
          
          <div className="bg-surface-50 rounded-lg p-4 border border-border-light">
            <div className="flex items-center space-x-2">
              <Icon name="DollarSign" size={16} className="text-warning" />
              <span className="text-sm font-medium text-text-secondary uppercase tracking-wide">Total Cost</span>
            </div>
            <p className="text-2xl font-bold text-text-primary mt-2">
              {formatCurrency(totalCost)}
            </p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-surface-50 border-b border-border">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-surface-100 transition-colors duration-150"
                onClick={() => handleSort('date')}
              >
                <div className="flex items-center space-x-1">
                  <span>Date</span>
                  {getSortIcon('date')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-surface-100 transition-colors duration-150"
                onClick={() => handleSort('shipmentId')}
              >
                <div className="flex items-center space-x-1">
                  <span>Shipment</span>
                  {getSortIcon('shipmentId')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Customer
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-surface-100 transition-colors duration-150"
                onClick={() => handleSort('offsetType')}
              >
                <div className="flex items-center space-x-1">
                  <span>Offset Type</span>
                  {getSortIcon('offsetType')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-surface-100 transition-colors duration-150"
                onClick={() => handleSort('amount')}
              >
                <div className="flex items-center space-x-1">
                  <span>Amount</span>
                  {getSortIcon('amount')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-surface-100 transition-colors duration-150"
                onClick={() => handleSort('cost')}
              >
                <div className="flex items-center space-x-1">
                  <span>Cost</span>
                  {getSortIcon('cost')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-surface-100 transition-colors duration-150"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center space-x-1">
                  <span>Status</span>
                  {getSortIcon('status')}
                </div>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-surface divide-y divide-border">
            {sortedHistory.map((offset) => (
              <tr key={offset.id} className="hover:bg-surface-50 transition-colors duration-150">
                <td className="px-6 py-4">
                  <div className="text-sm text-text-primary font-medium">
                    {formatDate(offset.date)}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="Package" size={16} className="text-primary" />
                    <div>
                      <p className="text-sm font-medium text-text-primary font-mono">
                        {offset.shipmentId}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-text-primary">
                    {offset.customer}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name={getOffsetTypeIcon(offset.offsetType)} 
                      size={16} 
                      className={getOffsetTypeColor(offset.offsetType)} 
                    />
                    <div>
                      <p className="text-sm font-medium text-text-primary">
                        {offset.offsetType}
                      </p>
                      <p className="text-xs text-text-secondary">
                        {offset.project}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-text-primary">
                    {offset.amount} tonnes CO2e
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-text-primary">
                    {formatCurrency(offset.cost)}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(offset.status)}`}>
                    {offset.status === 'verified' && <Icon name="CheckCircle" size={12} className="mr-1" />}
                    {offset.status === 'pending' && <Icon name="Clock" size={12} className="mr-1" />}
                    {offset.status === 'processing' && <Icon name="Loader" size={12} className="mr-1 animate-spin" />}
                    {offset.status.charAt(0).toUpperCase() + offset.status.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      className="p-1 text-text-secondary hover:text-primary transition-colors duration-150"
                      title="View certificate"
                    >
                      <Icon name="FileText" size={16} />
                    </button>
                    <button
                      className="p-1 text-text-secondary hover:text-primary transition-colors duration-150"
                      title="Download certificate"
                    >
                      <Icon name="Download" size={16} />
                    </button>
                    <button
                      className="p-1 text-text-secondary hover:text-primary transition-colors duration-150"
                      title="More options"
                    >
                      <Icon name="MoreHorizontal" size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {sortedHistory.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Leaf" size={48} className="text-text-secondary mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">No offset history found</h3>
          <p className="text-text-secondary mb-6">
            {offsetHistory.length === 0 
              ? "You haven't purchased any carbon offsets yet." :"No offsets match your current filters."
            }
          </p>
          <button
            onClick={() => onPurchaseOffset(null)}
            className="bg-success text-white px-6 py-2 rounded-md font-medium hover:bg-success-600 transition-colors duration-150 flex items-center space-x-2 mx-auto"
          >
            <Icon name="Plus" size={16} />
            <span>Purchase Your First Offset</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default OffsetHistoryTable;